<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LicenseScreen from './components/LicenseScreen.vue';
import LobbyScreen from './components/LobbyScreen.vue';
import LoginScreen from './components/LoginScreen.vue';
import GameScreen from './components/GameScreen.vue';
import WanliLoadingScreen from './components/WanliLoadingScreen.vue';
import { runtimeLogger } from './services/runtimeLogger.js';
import { useLegacyI18n } from './composables/useLegacyI18n.js';
import { createDesktopDataService, normalizeLobbySnapshot } from './services/desktopDataService.js';
import { createDemoSnapshot } from './services/demoData.js';
import { createGameAudio } from './services/gameAudio.js';
import { releaseAssetUrl } from './services/runtimeAssets.js';
import { createScreenWakeLock } from './services/screenWakeLock.js';
import { calculateKeyboardScrollDelta, isEditableElement, resolveKeyboardHeight } from './domain/mobileViewport.js';
import {
  SHORT_BACKGROUND_MAX_MS,
  classifyBackgroundDuration,
  shouldClearPendingBets,
  shouldDisconnectImmediately
} from './domain/pageLifecycle.js';

const query = new URLSearchParams(window.location.search);
const isMobile = Boolean(window.__WANLI_MOBILE__);
const dataMode = query.get('mode') === 'demo' ? 'demo' : 'live';
const allowedScreens = ['login', 'license', 'hall', 'game'];
const requestedScreen = allowedScreens.includes(query.get('screen')) ? query.get('screen') : 'login';
const screen = ref(dataMode === 'demo' ? requestedScreen : 'login');
const busy = ref(false);
const booting = ref(true);
const error = ref('');
const notice = ref('');
const recovering = ref(false);
const recoveryDetail = ref('');
const resumeEvent = ref({ id: 0, mode: '', clearPending: false, betOutcomeUnknown: false });
const selectedTableIndex = ref(Math.max(0, Math.min(3, Number(query.get('table')) || 0)));
const requestedGameOverlay = ref('');
const snapshot = ref(normalizeLobbySnapshot({}));
const service = createDesktopDataService({ mode: dataMode, clientType: isMobile ? 'mobile' : 'desktop' });
const { language, languages, t, setLanguage, agreementParagraphs, flagUrl } = useLegacyI18n();
const audio = createGameAudio({ language: language.value });
const screenWakeLock = createScreenWakeLock({ enabled: isMobile, logger: runtimeLogger });
const muted = ref(false);
let unsubscribe = null;
let unsubscribeIdleReturn = null;
let noticeTimer = 0;
let mobileLayoutWidth = window.innerWidth;
let mobileLayoutHeight = window.innerHeight;
let mobileLayoutLandscape = mobileLayoutWidth > mobileLayoutHeight;
let mobileKeyboardTimers = [];
let hiddenContext = null;
let lastResumeAt = 0;
let recoveryPromise = null;
let recoveryRetryTimer = 0;
let backgroundDisconnectTimer = 0;
const clientType = isMobile ? 'mobile' : 'desktop';
const viewStorageKey = `wanli.vue.view.v1:${clientType}`;
const criticalAssets = [
  isMobile
    ? '/assets/brand/wanli-phaser-inspired-mobile-login-bg-v1.webp'
    : '/assets/brand/wanli-phaser-inspired-login-bg-v8-three-gold-bitcoins.webp',
  '/assets/brand/wanli-yongli-clean-hall-bg-emerald-v1.webp',
  '/assets/brand/wanli-script-gold-v1.webp',
  ...[50, 100, 500, 1000, 5000, 10000, 50000, 'allin'].map((value) => `/assets/brand/chips/wanli-chip-${value}-v2.webp`)
].map(releaseAssetUrl);

function preloadImage(src) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ src, ok: true });
    image.onerror = () => {
      runtimeLogger.error('[wanli-loader] critical asset failed', { src });
      resolve({ src, ok: false });
    };
    image.src = src;
  });
}

async function completeBoot() {
  const started = performance.now();
  const tasks = criticalAssets.map(preloadImage);
  if (document.fonts?.ready) tasks.push(document.fonts.ready.catch((error) => {
    runtimeLogger.error('[wanli-loader] font preload failed', error);
  }));
  await Promise.all(tasks);
  const remaining = Math.max(0, 950 - (performance.now() - started));
  if (remaining) await new Promise((resolve) => window.setTimeout(resolve, remaining));
  booting.value = false;
}
const fitStage = () => {
  const viewport = window.visualViewport;
  const width = viewport?.width || window.innerWidth;
  const height = viewport?.height || window.innerHeight;
  if (isMobile) {
    const focused = isEditableElement(document.activeElement);
    const keyboardHeight = resolveKeyboardHeight({
      focused,
      virtualHeight: navigator.virtualKeyboard?.boundingRect?.height || 0,
      visualHeight: viewport?.height || height,
      layoutHeight: mobileLayoutHeight
    });
    const keyboardOpen = keyboardHeight > 0;
    const currentLandscape = window.innerWidth > window.innerHeight;
    if (!keyboardOpen && currentLandscape !== mobileLayoutLandscape) {
      mobileLayoutWidth = window.innerWidth;
      mobileLayoutHeight = window.innerHeight;
      mobileLayoutLandscape = currentLandscape;
    }
    const stageWidth = 1080;
    const stageHeight = 1920;
    document.documentElement.style.setProperty('--mobile-layout-width', `${mobileLayoutWidth}px`);
    document.documentElement.style.setProperty('--mobile-layout-height', `${mobileLayoutHeight}px`);
    document.documentElement.style.setProperty('--mobile-stage-width', `${stageWidth}px`);
    document.documentElement.style.setProperty('--mobile-stage-height', `${stageHeight}px`);
    document.documentElement.style.setProperty('--stage-scale-x', String(mobileLayoutWidth / stageWidth));
    document.documentElement.style.setProperty('--stage-scale-y', String(mobileLayoutHeight / stageHeight));
    window.requestAnimationFrame(updateMobileKeyboardPosition);
    return;
  }
  document.documentElement.style.setProperty('--stage-scale', String(Math.min(width / 1440, height / 900)));
};

function updateMobileKeyboardPosition() {
  if (!isMobile) return;
  const viewport = window.visualViewport;
  const active = document.activeElement;
  const keyboardHeight = resolveKeyboardHeight({
    focused: isEditableElement(active),
    virtualHeight: navigator.virtualKeyboard?.boundingRect?.height || 0,
    visualHeight: viewport?.height || window.innerHeight,
    layoutHeight: mobileLayoutHeight
  });
  const open = keyboardHeight > 0;
  document.documentElement.style.setProperty('--mobile-keyboard-height', `${keyboardHeight}px`);
  document.body.classList.toggle('mobile-keyboard-open', open);
  const scroller = document.getElementById('app');
  if (!scroller) return;
  if (!open) {
    scroller.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const virtualTop = Number(navigator.virtualKeyboard?.boundingRect?.y) || 0;
  const viewportBottom = (viewport?.offsetTop || 0) + (viewport?.height || mobileLayoutHeight);
  const visibleBottom = virtualTop > 0
    ? virtualTop
    : Math.min(viewportBottom, mobileLayoutHeight - keyboardHeight);
  const delta = calculateKeyboardScrollDelta({
    rect: active.getBoundingClientRect(),
    visibleTop: viewport?.offsetTop || 0,
    visibleBottom,
    padding: 28
  });
  if (delta) scroller.scrollBy({ top: delta, behavior: 'smooth' });
}

function handleMobileFocusChange() {
  mobileKeyboardTimers.forEach(window.clearTimeout);
  mobileKeyboardTimers = [0, 120, 320, 620].map((delay) => window.setTimeout(fitStage, delay));
}

function currentRoundKey(tableIndex = selectedTableIndex.value) {
  return String(snapshot.value.tables?.[tableIndex]?.lastSetId || '');
}

function readPersistedView() {
  try { return JSON.parse(sessionStorage.getItem(viewStorageKey) || 'null'); } catch (storageError) {
    runtimeLogger.warn('[wanli-lifecycle] persisted view could not be read', storageError);
    return null;
  }
}

function persistView(hiddenAt = 0) {
  if (dataMode !== 'live' || !service.hasRestorableSession()) return;
  try {
    sessionStorage.setItem(viewStorageKey, JSON.stringify({
      screen: screen.value,
      tableIndex: selectedTableIndex.value,
      roundKey: currentRoundKey(),
      hiddenAt: Math.max(0, Number(hiddenAt) || 0),
      betOutcomeUnknown: Boolean(hiddenContext?.betOutcomeUnknown),
      updatedAt: Date.now()
    }));
  } catch (storageError) {
    runtimeLogger.warn('[wanli-lifecycle] persisted view could not be written', storageError);
  }
}

function clearPersistedView() {
  try { sessionStorage.removeItem(viewStorageKey); } catch (storageError) {
    runtimeLogger.warn('[wanli-lifecycle] persisted view could not be cleared', storageError);
  }
}

function handlePageHidden(reason = 'hidden') {
  if (dataMode !== 'live' || !service.hasRestorableSession()) return;
  if (!hiddenContext) {
    hiddenContext = {
      hiddenAt: Date.now(),
      screen: screen.value,
      tableIndex: selectedTableIndex.value,
      roundKey: currentRoundKey(),
      betOutcomeUnknown: service.hasPendingSocketRequest('TouZhuProc')
    };
  }
  audio.stopAll();
  window.clearTimeout(backgroundDisconnectTimer);
  const disconnectImmediately = shouldDisconnectImmediately(reason);
  service.suspendForBackground(reason, { disconnect: disconnectImmediately });
  if (!disconnectImmediately) {
    backgroundDisconnectTimer = window.setTimeout(() => {
      backgroundDisconnectTimer = 0;
      if (!document.hidden) return;
      service.suspendForBackground('background-timeout', { disconnect: true });
    }, SHORT_BACKGROUND_MAX_MS);
  }
  hiddenContext.betOutcomeUnknown = hiddenContext.betOutcomeUnknown || service.backgroundBetOutcomeUnknown;
  persistView(hiddenContext.hiddenAt);
  runtimeLogger.info('[wanli-lifecycle] page hidden', { reason, clientType, screen: screen.value });
}

function isInvalidSessionError(recoveryError) {
  return Boolean(recoveryError?.sessionInvalid || /(?:LOGIN|RECONNECT|SESSION).*(?:INVALID|EXPIRED|MISSING|REPLACED)/i.test(String(recoveryError?.code || '')));
}

async function recoverSession(reason = 'visible', { initial = false } = {}) {
  if (dataMode !== 'live' || document.hidden || !service.hasRestorableSession()) return false;
  if (recoveryPromise) return recoveryPromise;
  const now = Date.now();
  const persisted = readPersistedView();
  // Debounce duplicate focus/pageshow events, not a genuinely new hide/return.
  if (!initial && !hiddenContext && !persisted?.hiddenAt && lastResumeAt && now - lastResumeAt < 500) return false;
  lastResumeAt = now;
  window.clearTimeout(recoveryRetryTimer);
  window.clearTimeout(backgroundDisconnectTimer);
  backgroundDisconnectTimer = 0;
  const context = hiddenContext || persisted || {};
  const hiddenDurationMs = context.hiddenAt ? Math.max(0, now - context.hiddenAt) : 0;
  const mode = classifyBackgroundDuration(hiddenDurationMs);
  recoveryPromise = (async () => {
    // The connected-socket path has no other await. Start after the promise is
    // assigned, so finally cannot clear it before the assignment recreates it.
    await Promise.resolve();
    try {
      if (mode === 'expired') {
        const expired = new Error(t('message.sessionExpired', '登录会话已过期，请重新登录'));
        expired.code = 'RECONNECT_TICKET_EXPIRED';
        expired.sessionInvalid = true;
        throw expired;
      }
      let recoveryPath = 'full-reconnect';
      if (mode === 'short' && service.resumeConnectedSession(reason)) {
        recoveryPath = 'kept-socket';
      } else {
        recovering.value = true;
        recoveryDetail.value = t('message.reconnectAttempting', '正在重新连接并同步桌台数据...');
        const status = await service.verifySessionStatus();
        if (!status?.valid) {
          const invalid = new Error(status?.message || t('message.sessionExpired', '登录会话已失效，请重新登录'));
          invalid.code = status?.code || 'SESSION_INVALID';
          invalid.sessionInvalid = !status?.transient;
          throw invalid;
        }
        await service.reconnectSession();
      }
      const restoredTableIndex = Math.max(0, Math.min(3, Number(context.tableIndex) || 0));
      selectedTableIndex.value = restoredTableIndex;
      if (initial) {
        screen.value = ['license', 'hall', 'game'].includes(context.screen) ? context.screen : 'hall';
      }
      const table = snapshot.value.tables?.[restoredTableIndex];
      const betOutcomeUnknown = Boolean(context.betOutcomeUnknown || service.consumeBackgroundBetOutcomeUnknown());
      resumeEvent.value = {
        id: resumeEvent.value.id + 1,
        mode,
        recoveryPath,
        clearPending: betOutcomeUnknown || shouldClearPendingBets({
          mode,
          previousRoundKey: context.roundKey,
          currentRoundKey: table?.lastSetId,
          bettingOpen: Boolean(table?.bettingOpen)
        }),
        betOutcomeUnknown
      };
      hiddenContext = null;
      persistView(0);
      recovering.value = false;
      recoveryDetail.value = '';
      runtimeLogger.info('[wanli-lifecycle] foreground recovery complete', {
        reason,
        clientType,
        mode,
        recoveryPath,
        resumeEventId: resumeEvent.value.id,
        hiddenDurationMs,
        tableIndex: restoredTableIndex,
        clearPending: resumeEvent.value.clearPending
      });
      return true;
    } catch (recoveryError) {
      runtimeLogger.error('[wanli-lifecycle] foreground recovery failed', {
        reason,
        clientType,
        mode,
        hiddenDurationMs,
        code: recoveryError?.code,
        message: recoveryError?.message
      });
      if (isInvalidSessionError(recoveryError)) {
        service.logout();
        clearPersistedView();
        hiddenContext = null;
        recovering.value = false;
        recoveryDetail.value = '';
        screen.value = 'login';
        error.value = recoveryError?.message || t('message.sessionExpired', '登录会话已失效，请重新登录');
        return false;
      }
      recoveryDetail.value = t('message.reconnectFailed', '网络尚未恢复，正在继续尝试...');
      recoveryRetryTimer = window.setTimeout(() => {
        if (!document.hidden) void recoverSession('retry');
      }, 3000);
      return false;
    } finally {
      recoveryPromise = null;
    }
  })();
  return recoveryPromise;
}

function handleVisibilityChange() {
  if (document.hidden) handlePageHidden('visibilitychange');
  else void recoverSession('visibilitychange');
}

function handlePageShow() {
  if (!document.hidden) void recoverSession('pageshow');
}

function handleWindowFocus() {
  if (!document.hidden && (hiddenContext || readPersistedView()?.hiddenAt)) void recoverSession('focus');
}

function handlePageHide() {
  handlePageHidden('pagehide');
}

function handlePageFreeze() {
  handlePageHidden('freeze');
}
const handleSessionEnded = (event) => {
  clearPersistedView();
  recovering.value = false;
  error.value = window.AppI18n?.translateMessage?.(event.detail?.message, t('message.duplicateLoginKicked', 'This account is signed in on another device.'), 'message.duplicateLoginKicked') || t('message.duplicateLoginKicked', 'This account is signed in on another device.');
  screen.value = 'login';
};

const pageClass = computed(() => ({
  'mobile-page': isMobile,
  'show-license': screen.value === 'license',
  'show-hall': screen.value === 'hall',
  'show-game': screen.value === 'game'
}));

function showNotice(message) {
  notice.value = message;
  window.clearTimeout(noticeTimer);
  noticeTimer = window.setTimeout(() => { notice.value = ''; }, 1800);
}

async function prepareVerification(host) {
  try {
    await service.prepareVerification(host);
  } catch (verificationError) {
    error.value = window.AppI18n?.translateMessage?.(verificationError?.message, t('message.turnstileLoadFailed', 'Failed to load verification.'), 'message.turnstileLoadFailed') || t('message.turnstileLoadFailed', 'Failed to load verification.');
  }
}

async function login(credentials) {
  muted.value = false;
  audio.setMuted(false);
  audio.play('click', { queue: false });
  busy.value = true;
  error.value = '';
  try {
    await service.login(credentials);
    screen.value = 'license';
  } catch (loginError) {
    error.value = window.AppI18n?.translateMessage?.(loginError?.message, t('message.loginFailedRetry', 'Login failed. Please try again.'), 'message.loginFailedRetry') || t('message.loginFailedRetry', 'Login failed. Please try again.');
    if (loginError?.code === 'CLIENT_VERSION_OUTDATED' && loginError?.refreshRequired) {
      window.setTimeout(() => window.location.reload(), 1200);
    }
  } finally {
    busy.value = false;
  }
}

function agree() {
  audio.play('click', { queue: false });
  if (!snapshot.value.tables.length) {
    error.value = t('vueDesktop.message.hallUnavailable', 'Lobby data is not ready. Please sign in again.');
    screen.value = 'login';
    return;
  }
  screen.value = 'hall';
  window.setTimeout(() => audio.play('welcome'), 120);
}

function decline() {
  audio.play('click', { queue: false });
  service.logout();
  clearPersistedView();
  screen.value = 'login';
}

function logout() {
  audio.play('click', { queue: false });
  service.logout();
  clearPersistedView();
  screen.value = 'login';
  error.value = '';
}

function selectTable(index) {
  const table = snapshot.value.tables[index];
  if (!table) return;
  audio.play('click', { queue: false });
  selectedTableIndex.value = index;
  requestedGameOverlay.value = '';
  service.markManualVideoEntry(index);
  screen.value = 'game';
  window.dispatchEvent(new CustomEvent('wanli-table-selected', { detail: { index, table } }));
}

function hallAction(action) {
  audio.play('click', { queue: false });
  requestedGameOverlay.value = action === 'records' ? 'report' : 'password';
  selectedTableIndex.value = 0;
  screen.value = 'game';
}

function toggleMuted() {
  muted.value = !muted.value;
  audio.setMuted(muted.value);
}

function backFromGame() {
  audio.play('click', { queue: false });
  requestedGameOverlay.value = '';
  screen.value = 'hall';
}

function returnFromIdleRounds(event) {
  if (screen.value !== 'game') return;
  runtimeLogger.info('[wanli-idle] return to lobby and stop video', { clientType, ...event });
  audio.stopAll();
  requestedGameOverlay.value = '';
  // GameScreen/VideoSurface unmount closes the video socket, worker and timers.
  // Keep the business socket and login session alive for a manual table re-entry.
  screen.value = 'hall';
  if (hiddenContext) hiddenContext.screen = 'hall';
  persistView(0);
  showNotice(t('message.idleThreeRounds', '您已经三局未下注！'));
}

function closeRequestedGameOverlay() {
  requestedGameOverlay.value = '';
  screen.value = 'hall';
}

function changeGameTable(index) {
  service.markManualVideoEntry(index);
  audio.play('click', { queue: false });
  selectedTableIndex.value = index;
}

watch(language, (next) => audio.setLanguage(next));
watch([screen, selectedTableIndex], () => persistView(0));
watch([screen, selectedTableIndex, requestedGameOverlay], ([next, tableIndex, overlay]) => {
  service.setActiveTable(next === 'game' && !overlay ? tableIndex : null);
}, { flush: 'sync' });
watch(screen, (next, previous) => {
  if (next === 'hall') service.requestTableTime();
  void screenWakeLock.setActive(
    isMobile && next === 'game',
    `screen:${previous || 'bootstrap'}->${next}`
  );
}, { immediate: true });

onMounted(async () => {
  void completeBoot();
  audio.prime();
  fitStage();
  window.addEventListener('resize', fitStage);
  window.visualViewport?.addEventListener('resize', fitStage);
  window.visualViewport?.addEventListener('scroll', updateMobileKeyboardPosition);
  document.addEventListener('focusin', handleMobileFocusChange);
  document.addEventListener('focusout', handleMobileFocusChange);
  navigator.virtualKeyboard?.addEventListener?.('geometrychange', fitStage);
  unsubscribe = service.subscribe((next) => { snapshot.value = next; });
  unsubscribeIdleReturn = service.subscribeIdleReturn(returnFromIdleRounds);
  if (dataMode === 'demo' && requestedScreen !== 'login') {
    service.raw = createDemoSnapshot('00000005');
    service.emitSnapshot();
    service.startCountdowns();
    if (screen.value === 'game') service.setActiveTable(selectedTableIndex.value);
  }
  window.addEventListener('wanli-session-ended', handleSessionEnded);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('freeze', handlePageFreeze);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('focus', handleWindowFocus);
  window.addEventListener('online', handlePageShow);
  if (dataMode === 'live' && service.hasRestorableSession() && !document.hidden) {
    await recoverSession(document.wasDiscarded ? 'discarded-reload' : 'bootstrap', { initial: true });
  }
  const runtime = { service, screen, snapshot, dataMode, audio, isMobile, recovering, resumeEvent };
  window[isMobile ? '__WANLI_VUE_MOBILE__' : '__WANLI_VUE_DESKTOP__'] = runtime;
});

onBeforeUnmount(() => {
  void screenWakeLock.destroy();
  unsubscribe?.();
  unsubscribeIdleReturn?.();
  service.logout({ clearSession: false });
  window.clearTimeout(recoveryRetryTimer);
  window.clearTimeout(backgroundDisconnectTimer);
  window.clearTimeout(noticeTimer);
  window.removeEventListener('resize', fitStage);
  window.visualViewport?.removeEventListener('resize', fitStage);
  window.visualViewport?.removeEventListener('scroll', updateMobileKeyboardPosition);
  document.removeEventListener('focusin', handleMobileFocusChange);
  document.removeEventListener('focusout', handleMobileFocusChange);
  navigator.virtualKeyboard?.removeEventListener?.('geometrychange', fitStage);
  mobileKeyboardTimers.forEach(window.clearTimeout);
  window.removeEventListener('wanli-session-ended', handleSessionEnded);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  document.removeEventListener('freeze', handlePageFreeze);
  window.removeEventListener('pagehide', handlePageHide);
  window.removeEventListener('pageshow', handlePageShow);
  window.removeEventListener('focus', handleWindowFocus);
  window.removeEventListener('online', handlePageShow);
  audio.destroy();
});
</script>

<template>
  <main class="page vue-page" :class="pageClass">
    <div class="stone" />
    <div class="ambient-lights" aria-hidden="true" />
    <div class="rail-glint" aria-hidden="true" />
    <div class="motes" aria-hidden="true">
      <span class="mote" style="left:18%;top:30%;--duration:16s;--delay:-4s;--drift:17px" />
      <span class="mote" style="left:31%;top:54%;--duration:19s;--delay:-12s;--drift:-13px" />
      <span class="mote" style="left:47%;top:25%;--duration:17s;--delay:-8s;--drift:11px" />
      <span class="mote" style="left:61%;top:49%;--duration:21s;--delay:-15s;--drift:-16px" />
      <span class="mote" style="left:79%;top:34%;--duration:18s;--delay:-6s;--drift:14px" />
    </div>
    <LoginScreen
      v-if="screen === 'login'"
      :language="language"
      :languages="languages"
      :flag-url="flagUrl"
      :t="t"
      :busy="busy"
      :error="error"
      :data-mode="dataMode"
      :audio="audio"
      @language="setLanguage"
      @login="login"
      @verification-host="prepareVerification"
    />
    <LicenseScreen
      v-else-if="screen === 'license'"
      :language="language"
      :t="t"
      :paragraphs="agreementParagraphs"
      @agree="agree"
      @decline="decline"
    />
    <LobbyScreen
      v-else-if="screen === 'hall'"
      :snapshot="snapshot"
      :t="t"
      :notice="notice"
      @table="selectTable"
      @logout="logout"
      @action="hallAction"
    />
    <GameScreen
      v-else
      :snapshot="snapshot"
      :service="service"
      :language="language"
      :table-index="selectedTableIndex"
      :data-mode="dataMode"
      :t="t"
      :audio="audio"
      :muted="muted"
      :mobile="isMobile"
      :initial-overlay="requestedGameOverlay"
      :resume-event="resumeEvent"
      @back="backFromGame"
      @logout="logout"
      @table-change="changeGameTable"
      @toggle-muted="toggleMuted"
      @initial-overlay-close="closeRequestedGameOverlay"
    />
    <Transition name="wanli-loader">
      <WanliLoadingScreen
        v-if="booting || busy || recovering"
        :label="recovering ? t('message.reconnectAttempting', '正在恢复桌台连接') : busy ? t('vueDesktop.status.verifyingMember', 'Verifying member identity') : t('vueDesktop.status.preparingLobby', 'Preparing the VIP lobby')"
        :detail="recovering ? recoveryDetail : busy ? t('status.verifyingLogin', 'Verifying login...') : t('status.enteringHall', 'Entering lobby...')"
      />
    </Transition>
  </main>
  <div v-if="isMobile" class="mobile-keyboard-spacer" aria-hidden="true" />
</template>
